/** Painel lateral para criar/editar tarefas — apenas apresentação. */
import { CheckIcon } from '../../../components/icons';
import Input from '../../../components/ui/Input';
import DateTimeField from '../../../components/ui/DateTimeField';
import Textarea from '../../../components/ui/Textarea';
import Checkbox from '../../../components/ui/Checkbox';
import { Button } from '../../../components/ui/Button';
import { FieldGroup } from '../../../components/ui/field';
import { SidebarHeader } from '../../kanban/components/SidebarHeader';
import { Task } from '../../../types';
import { cn } from '@/lib/utils';
import { useTaskFormSheet } from '../hooks/useTaskFormSheet';
import { TaskFormLeadSelect } from './TaskFormLeadSelect';
import { TASK_TITLE_MIN } from '../lib/taskFormPayload';

interface TaskFormSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (taskData: Partial<Task>) => Promise<void>;
  task?: Task | null;
}

export default function TaskFormSheet({ isOpen, onClose, onSave, task }: TaskFormSheetProps) {
  const form = useTaskFormSheet({ isOpen, task, onSave });
  const sheetTitle = form.isEditing ? 'Editar Tarefa' : 'Nova Tarefa';

  if (!isOpen) return null;

  return (
    <>
      <button
        type="button"
        className={cn(
          'fixed inset-0 z-overlay overlay-premium',
          'transition-base motion-reduce:transition-none',
          'cursor-default',
        )}
        onClick={onClose}
        aria-label="Fechar formulário de tarefa"
      />

      <aside
        className={cn(
          'fixed top-0 right-0 z-modal panel-sidebar flex h-full w-full flex-col',
          'transition-base motion-reduce:transition-none',
          'sm:max-w-[31.25rem]',
        )}
        role="dialog"
        aria-modal="true"
        aria-label={sheetTitle}
      >
        <SidebarHeader
          title={sheetTitle}
          subtitle={
            form.isEditing
              ? 'Atualize as informações da tarefa.'
              : 'Preencha os dados para criar uma nova tarefa.'
          }
          onClose={onClose}
        />

        <form
          onSubmit={form.handleSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          noValidate
        >
          <FieldGroup className="scrollbar-premium min-h-0 flex-1 overflow-y-auto px-s4 py-s5 sm:px-s6 sm:py-s6">
            <Input
              label="Título *"
              type="text"
              name="title"
              value={form.formData.title}
              onChange={form.handleChange}
              placeholder="Digite o título da tarefa..."
              required
              minLength={TASK_TITLE_MIN}
              maxLength={form.titleMaxLength}
            />
            <Textarea
              label="Descrição"
              name="description"
              value={form.formData.description}
              onChange={form.handleChange}
              placeholder="Detalhes da tarefa (opcional)"
              minRows={3}
              maxLength={form.descriptionMaxLength}
            />
            <TaskFormLeadSelect
              selectedLeadName={form.selectedLeadName}
              leadSearchTerm={form.leadSearchTerm}
              isLoadingLeads={form.isLoadingLeads}
              filteredLeads={form.filteredLeads}
              showLeadList={form.showLeadList}
              showLeadEmpty={form.showLeadEmpty}
              onSearchChange={value => {
                form.setLeadSearchTerm(value);
                form.setShowLeadDropdown(true);
              }}
              onSearchFocus={() => form.setShowLeadDropdown(true)}
              onSelectLead={form.selectLead}
              onClearLead={form.clearLead}
            />
            <DateTimeField
              date={form.formData.dueDate}
              time={form.formData.dueTime}
              dateLabel="Data de Vencimento"
              timeName="dueTime"
              dateName="dueDate"
              groupLabel="Data e horário de vencimento"
              onChange={form.handleDateTimeChange}
            />
            <Checkbox
              id="task-sheet-isCompleted"
              name="isCompleted"
              checked={form.formData.isCompleted}
              onChange={form.handleChange}
              label="Marcar como concluída"
              checkboxSize="md"
            />
          </FieldGroup>

          <footer
            className={cn(
              'shrink-0 border-t border-subtle bg-surface-subtle',
              'px-s4 py-s4 sm:px-s6',
            )}
          >
            <Button type="submit" size="lg" className="w-full gap-s2" disabled={form.isSaving}>
              <CheckIcon className="size-5 shrink-0" aria-hidden />
              {form.isSaving ? 'Salvando...' : form.isEditing ? 'Salvar Alterações' : 'Criar Tarefa'}
            </Button>
          </footer>
        </form>
      </aside>
    </>
  );
}
